import { createContext, useEffect, useRef, useState } from "react";
import { useContext } from "react";
import UtilsEdit from "@utils/utilsEdit";
import API from "@api/api";
import Utils from '@utils/utils';
import { AppContext } from '@utils/AppContext';

export const ImageResizeContext = createContext();

export const ImageResizeContextProvider = (props) => {
    const {
        setGlobalTaskReadFlag,
        setSelectedTaskListId,
        setAIEditRecentTaskId,
        setAIEditTempProjectId: setAppAIEditTempProjectId
    } = useContext(AppContext)
    const [showCropType, setShowCropType] = useState(1)
    const [zoom, setZoom] = useState();
    const [initMinZoom, setInitMinZoom] = useState();
    const [initLeftCanvasZoom, setInitLeftCanvasZoom] = useState();
    const [initRightCanvasZoom, setInitRightCanvasZoom] = useState();
    const [imageUrl, setImageUrl] = useState();
    const [handleLoading, setHandleLoading] = useState(false);
    const [resizeResult, setResizeResult] = useState([]);
    const [imgWidth, setImgWidth] = useState();
    const [imgHeight, setImgHeight] = useState();
    const [targetSsize, setTargetSize] = useState([1024, 1024]);
    const [targetSizeCache, setTargetSizeCache] = useState([]);
    const [selectedKey, setSelectedKey] = useState();
    const [selectedKeyCache, setSelectedKeyCache] = useState();
    const [initImgRatio, setInitImgRatio] = useState();
    const [downBtnLoading, setDownBtnLoading] = useState(false);
    const [AIEditTemprojectId, setAIEditTempProjectId] = useState();
    const [AIEditTemTaskId, setAIEditTempTaskId] = useState();
    const [init, setInit] = useState(false)

    const prevInitialImageUrlRef = useRef()
    const onImageChangeRef = useRef(props.onImageChange)

    const resetResizeState = () => {
        setImageUrl()
        setResizeResult([])
        setImgWidth()
        setImgHeight()
        setTargetSize([1024, 1024])
        setTargetSizeCache([])
        setSelectedKey()
        setSelectedKeyCache()
        setInitImgRatio()
        setShowCropType(1)
        setZoom()
        setInitMinZoom()
        setInitLeftCanvasZoom()
        setInitRightCanvasZoom()
        setHandleLoading(false)
        setDownBtnLoading(false)
        setAIEditTempProjectId()
        setAIEditTempTaskId()
    }

    const resizeCacheHandler = () => {
        sessionStorage.setItem(`imgResizeCache`, JSON.stringify({
            resizeResult: [...resizeResult],
            imageUrl,
            imgWidth,
            imgHeight,
            targetSizeCache,
            selectedKeyCache,
            initImgRatio,
            AIEditTemprojectId,
            AIEditTemTaskId,
        }))
    }

    const resizeCacheRestore = () => {
        const imgResizeCacheStr = sessionStorage.getItem(`imgResizeCache`)
        if (!imgResizeCacheStr) return

        const imgResizeCache = JSON.parse(imgResizeCacheStr)
        setImageUrl(imgResizeCache?.imageUrl)
        setAIEditTempProjectId(imgResizeCache?.AIEditTemprojectId)
        setAIEditTempTaskId(imgResizeCache?.AIEditTemTaskId)
        setImgWidth(imgResizeCache?.imgWidth)
        setImgHeight(imgResizeCache?.imgHeight)
        setSelectedKey(imgResizeCache?.selectedKeyCache)
        setSelectedKeyCache(imgResizeCache?.selectedKeyCache)
        setInitImgRatio(imgResizeCache?.initImgRatio)
        setTargetSize(
            [...(imgResizeCache?.targetSizeCache || [])].length === 0
                ? [1024, 1024]
                : [...imgResizeCache.targetSizeCache]
        )
        setTargetSizeCache([...(imgResizeCache?.targetSizeCache || [])])
        setResizeResult([...(imgResizeCache?.resizeResult || [])])
    }

    useEffect(() => {
        if (!init) {
            resizeCacheRestore()
            setInit(true)
            return
        }

        if (imageUrl) {
            resizeCacheHandler();
        }
    }, [
        resizeResult,
        imageUrl,
        imgWidth,
        targetSizeCache,
        selectedKeyCache,
        init
    ]);

    useEffect(() => {
        const loadImageSize = async () => {
            if (!imageUrl) return
            const imgSize = await UtilsEdit.getImageDimensions(imageUrl)
            setImgWidth(imgSize.width)
            setImgHeight(imgSize.height)
        }

        loadImageSize()
    }, [imageUrl])

    useEffect(() => {
        if (!props.initialImageUrl) {
            prevInitialImageUrlRef.current = undefined
            resetResizeState()
            return
        }

        if (props.initialImageUrl === prevInitialImageUrlRef.current) return

        prevInitialImageUrlRef.current = props.initialImageUrl
        setImageUrl(props.initialImageUrl)
        setAIEditTempProjectId(props.initialProjectId)
        setAIEditTempTaskId(props.initialTaskId)
        setResizeResult([])
        setTargetSize([1024, 1024])
        setTargetSizeCache([])
        setSelectedKey()
        setSelectedKeyCache()
        setShowCropType(1)
        setZoom()
        setInitMinZoom()
        setInitLeftCanvasZoom()
        setInitRightCanvasZoom()
    }, [props.initialImageUrl])

    useEffect(() => {
        setAIEditTempProjectId(props.initialProjectId)
        setAIEditTempTaskId(props.initialTaskId)
    }, [props.initialProjectId, props.initialTaskId])

    useEffect(() => {
        onImageChangeRef.current = props.onImageChange
    }, [props.onImageChange])

    useEffect(() => {
        if (imageUrl) {
            onImageChangeRef.current?.(imageUrl)
        }
    }, [imageUrl])

    function isBase64(src) {
        return /^data:image\/[a-z]+;base64,/.test(src);
    }

    const saveResultToProject = async (oriImage, resPath, projectId = '', imageType, taskId = '') => {
        if (isBase64(resPath)) {
            let formdata = new FormData
            formdata.append('file', Utils.convertBase64ToFile(resPath, 'image.png'))
            const res = await API.uploadImage(formdata)
            if (res.code != 0) throw new Error(res.message)
            resPath = res.data.filename
        }

        const metaRes = await API.generateMetadatav2({ image: resPath })
        if (!metaRes) return

        const res = await API.saveResultToProjectv2({
            projectId,
            taskId,
            imageType,
            clearAll: 0,
            imageList: [
                {
                    input: oriImage,
                    output: metaRes?.data?.[0],
                    noWatermarkImage: metaRes?.data?.[1]
                }
            ]
        })

        if (res.code != 0) throw new Error(res.message)
        const savedProjectId = res.data?.id
        const savedTaskId = res.data?.taskList?.[0]
        setAIEditTempProjectId(savedProjectId)
        setAIEditTempTaskId(savedTaskId)
        setAppAIEditTempProjectId(savedProjectId)
        setSelectedTaskListId(savedProjectId)
        setAIEditRecentTaskId(savedTaskId)
        setGlobalTaskReadFlag(prev => !prev)
        return res.data
    }

    return (
        <ImageResizeContext.Provider value={{
            showCropType, setShowCropType,
            zoom, setZoom,
            initMinZoom, setInitMinZoom,
            initLeftCanvasZoom, setInitLeftCanvasZoom,
            initRightCanvasZoom, setInitRightCanvasZoom,
            imageUrl, setImageUrl,
            handleLoading, setHandleLoading,
            resizeResult, setResizeResult,
            imgWidth, setImgWidth,
            imgHeight, setImgHeight,
            targetSsize, setTargetSize,
            selectedKey, setSelectedKey,
            initImgRatio, setInitImgRatio,
            targetSizeCache, setTargetSizeCache,
            downBtnLoading, setDownBtnLoading,
            selectedKeyCache, setSelectedKeyCache,
            saveResultToProject,
            AIEditTemprojectId, setAIEditTempProjectId,
            AIEditTemTaskId, setAIEditTempTaskId
        }}>
            {props.children}
        </ImageResizeContext.Provider>
    )
}
